/**
 * ConferenciaAvulsaForm — Modal form to create an avulsa conference.
 * User selects Cliente + Hospital from dropdowns.
 */
import React, { useState, useEffect } from 'react';
import apiClient from '../../services/apiClient';
import { X } from 'lucide-react';
import { ComboboxSelector } from '../ui/ComboboxSelector';
import { opcoesHospitalBuscavel, type HospitalBuscavel } from '../../utils/hospitalOptions';

interface Props {
  onSubmit: (clienteId: number, hospitalId: number) => Promise<void>;
  onClose: () => void;
}

interface OptionItem {
  id: number;
  nome: string;
}

export const ConferenciaAvulsaForm: React.FC<Props> = ({ onSubmit, onClose }) => {
  const [clientes, setClientes] = useState<OptionItem[]>([]);
  const [hospitais, setHospitais] = useState<HospitalBuscavel[]>([]);
  const [clienteId, setClienteId] = useState<number | ''>('');
  const [hospitalId, setHospitalId] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, hRes] = await Promise.all([
          apiClient.get('/clientes'),
          apiClient.get('/hospitais'),
        ]);

        // Extract from response — handle both array and paginated formats
        const clienteData = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.results || cRes.data?.items || []);
        const hospitalData = Array.isArray(hRes.data) ? hRes.data : (hRes.data?.results || hRes.data?.items || []);

        setClientes(clienteData.map((c: any) => ({
          id: c.id,
          nome: c.nome_fantasia || c.razao_social || c.nome || `#${c.id}`,
        })));
        // N135 (Alana): código e CNPJ descem junto para a busca poder casar por
        // eles — antes só o nome vinha, e achar o hospital na conferência de
        // lacres dependia de acertar o nome exato.
        setHospitais(hospitalData.map((h: any) => ({
          id: h.id,
          nome: h.nome_fantasia || h.nome || `#${h.id}`,
          codigo: h.codigo || h.codigo_erp || '',
          cnpj: h.cnpj || '',
        })));
      } catch (e) {
        console.error('Failed to load options:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSubmit = async () => {
    if (!clienteId || !hospitalId) return;
    setSubmitting(true);
    try {
      await onSubmit(clienteId as number, hospitalId as number);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            Conferência Avulsa
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Selecione o cliente e hospital para criar uma conferência avulsa (sem contagem vinculada).
        </p>

        {loading ? (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Carregando opções...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* N60 (Nicole): seletores com busca — a lista de hospitais era
                longa e sem filtro. Reusa o ComboboxSelector (busca embutida). */}
            <ComboboxSelector
              label="Cliente *"
              options={clientes.map(c => ({ id: c.id, label: c.nome }))}
              value={clienteId === '' ? 0 : clienteId}
              onChange={(id) => setClienteId(id)}
              placeholder="Selecione um cliente"
              searchPlaceholder="Buscar cliente..."
            />

            <ComboboxSelector
              label="Hospital *"
              options={opcoesHospitalBuscavel(hospitais)}
              value={hospitalId === '' ? 0 : hospitalId}
              onChange={(id) => setHospitalId(id)}
              placeholder="Selecione um hospital"
              searchPlaceholder="Buscar por nome, código ou CNPJ..."
            />
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!clienteId || !hospitalId || submitting}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Criando...' : 'Criar Conferência'}
          </button>
        </div>
      </div>
    </div>
  );
};
